import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle, Phone, ShieldCheck, ClipboardList, Clock, Lock } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { IntakeForm } from "../components/forms/IntakeForm";
import { CRISIS_RESOURCES } from "../data/content";

const expect = [
  { icon: Phone, title: "A conversation first", body: "We start by listening — no invasive screening, no judgment. Just understanding your situation." },
  { icon: ClipboardList, title: "A clear plan", body: "If Iron Shield is a fit, we build a stabilization plan with you and confirm next steps." },
  { icon: Clock, title: "A quick response", body: "We aim to respond within one business day. Urgent situations are prioritized." },
];

const Admissions = () => {
  const location = useLocation();
  const prefill = location.state?.prefill;
  const formRef = useRef(null);
  useEffect(() => { document.title = "Get Help | Iron Shield Veterans Reset Center"; }, []);
  useEffect(() => {
    if (prefill && formRef.current) {
      setTimeout(() => formRef.current.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [prefill]);
  return (
    <>
      <PageHero
        eyebrow="Get Help"
        lines={["Reaching out", "is the hard part.", "We'll take it from there."]}
        intro="If you're a veteran facing homelessness or crisis, you're in the right place. Fill out the form below and our intake team will follow up. Everything you share stays confidential."
      />

      {/* Emergency disclaimer */}
      <section className="bg-white border-b border-softgray" data-testid="emergency-disclaimer">
        <div className="container-x py-5 flex items-start gap-4">
          <AlertTriangle className="h-5 w-5 text-gold-deep shrink-0 mt-0.5" />
          <p className="text-sm text-charcoal/80 leading-relaxed">
            <span className="font-semibold text-charcoal">If you are in immediate danger, call 911.</span> Iron Shield is not an emergency service. For urgent mental-health support, contact the Veterans Crisis Line: dial <span className="font-semibold">988</span>, then press 1.
          </p>
        </div>
      </section>

      {/* What to expect */}
      <section className="py-20 md:py-24 bg-offwhite" data-testid="admissions-expect">
        <div className="container-x">
          <SectionHeading eyebrow="What to expect" title="A calm, human process." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {expect.map((e, i) => (
              <Reveal key={e.title} delay={i * 0.1} className="i-card p-8">
                <e.icon className="h-6 w-6 text-navy" strokeWidth={1.75} />
                <h3 className="mt-5 font-heading text-xl font-bold text-navy">{e.title}</h3>
                <p className="mt-3 text-charcoal/75 leading-relaxed text-sm">{e.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Form + sidebar */}
      <section className="py-16 md:py-20 bg-white border-y border-softgray" data-testid="admissions-form-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="Intake form" title="Tell us how to help." />
            <div className="mt-10" ref={formRef}>
              <IntakeForm prefill={prefill} />
            </div>
          </div>
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="i-card bg-offwhite p-8">
                <div className="flex items-center gap-3 mb-4">
                  <ShieldCheck className="h-5 w-5 text-navy" />
                  <div className="label-caps text-navy">Basic eligibility</div>
                </div>
                <ul className="space-y-2.5 text-sm text-charcoal/80">
                  <li>• U.S. military veteran</li>
                  <li>• Experiencing or at risk of homelessness</li>
                  <li>• Open to a stabilization plan</li>
                </ul>
              </div>

              <div className="i-card bg-offwhite p-8">
                <div className="label-caps text-navy mb-4">What you'll need</div>
                <p className="text-sm text-charcoal/80 leading-relaxed">Just your contact details and a short description of your situation. No documents required to start — we handle verification together, later.</p>
              </div>

              <div id="crisis" className="i-card-dark bg-navy text-offwhite p-8 scroll-mt-28" data-testid="crisis-resources">
                <div className="flex items-center gap-3 mb-5">
                  <Phone className="h-5 w-5 text-gold" />
                  <div className="label-caps text-gold">Crisis resources</div>
                </div>
                <ul className="space-y-4">
                  {CRISIS_RESOURCES.map((c) => (
                    <li key={c.label} className="border-l-2 border-gold/40 pl-4">
                      <div className="text-xs text-offwhite/60">{c.label}</div>
                      <div className="font-semibold">{c.value}</div>
                      <div className="text-xs text-offwhite/60 mt-0.5">{c.note}</div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex items-start gap-3 text-sm text-slate">
                <Lock className="h-4 w-4 shrink-0 mt-0.5" />
                <p>Your information is confidential and used only to coordinate your support. We never sell or share your data.</p>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export default Admissions;
